import React, { FC, Fragment, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useQuery } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import {
    subcontractCommunicationTemplate,
    translateSubcontractMessageTemplate,
} from 'apollo/templates/subcontract/subcontract.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { useSubcontractOrder } from 'pages/order-flow/subcontract-order/subcontract-order-context';
import InfoText from 'components/shared/info-text.component';
import { SubcontractMessageField } from 'models/client-directory';
import { LOCALE_LANGUAGE_OPTIONS, LocaleLanguage } from 'models/locale-format';
import CommunicationPanel from './communication-panel.component';
import { MESSAGE_FIELD_OPTIONS, MessageSection, buildSubcontractMessage } from './subcontract-message';

type CommunicationStepProps = {
    onBackClick: () => void;
    onNextClick: () => void;
};

type CommunicationData = {
    comments?: { message?: string; date?: string; user?: { fullName?: string }; version?: number }[];
    chatMessages?: { _id: string; senderName?: string; message?: string; createdAt?: string }[];
    messages?: {
        _id: string;
        originatorName?: { firstName?: string; lastName?: string };
        message?: string;
        createdAt?: string;
    }[];
    internalOrderDetails?: { notes?: string; externalNotes?: string } | null;
};

const noteEntries = (key: string, text: string | undefined) => (text?.trim() ? [{ key, text }] : []);

const messageSectionsOf = (data: CommunicationData | undefined): MessageSection[] => [
    {
        field: SubcontractMessageField.ORDER_COMMENTS,
        entries: (data?.comments ?? [])
            .filter((comment) => comment.message)
            .map((comment, index) => ({
                key: `comment-${index}`,
                author: comment.user?.fullName,
                date: comment.date,
                text: comment.message ?? '',
            })),
    },
    {
        field: SubcontractMessageField.INTERNAL_NOTES,
        entries: noteEntries('internal-notes', data?.internalOrderDetails?.notes),
    },
    {
        field: SubcontractMessageField.EXTERNAL_INSTRUCTIONS,
        entries: noteEntries('external-instructions', data?.internalOrderDetails?.externalNotes),
    },
    {
        field: SubcontractMessageField.CHAT,
        entries: (data?.chatMessages ?? []).map((chatMessage) => ({
            key: chatMessage._id,
            author: chatMessage.senderName,
            date: chatMessage.createdAt,
            text: chatMessage.message ?? '',
        })),
    },
    {
        field: SubcontractMessageField.LAB_CHAT,
        entries: (data?.messages ?? []).map((labMessage) => ({
            key: labMessage._id,
            author: [labMessage.originatorName?.firstName, labMessage.originatorName?.lastName]
                .filter(Boolean)
                .join(' '),
            date: labMessage.createdAt,
            text: labMessage.message ?? '',
        })),
    },
];

/**
 * DD-3484: this step shows the parent's communication read-only and the lab writes what the
 * subcontractor should know; whatever is written here becomes the sub-order's comment.
 * DD-3689: the message is filled from the selected boxes (the partner's defaults; none = a
 * blank message), and refilled when the selection changes — after asking, once the lab has
 * edited it. "Translate message" translates the text into the partner's language.
 */
const CommunicationStep: FC<CommunicationStepProps> = ({ onBackClick, onNextClick }) => {
    const { t: translate, i18n } = useTranslation(['orders', 'common', 'messages']);
    const { parentOrder, subcontract, updateSubcontract } = useSubcontractOrder();
    const { showBoundary } = useDenthubErrorBoundary();
    const [pendingFields, setPendingFields] = useState<SubcontractMessageField[] | null>(null);
    const [translationError, setTranslationError] = useState<string>();
    const messageLanguage = subcontract.messageLanguage ?? (i18n.language as LocaleLanguage);

    const fillMessage = (
        sections: MessageSection[],
        messageFields: SubcontractMessageField[],
        updates: Partial<{ isCommentPrefilled: boolean }> = {},
    ) => {
        const message = buildSubcontractMessage(sections, messageFields, translate);
        updateSubcontract({ messageFields, comment: message, generatedComment: message, ...updates });
    };

    const [translateMessage, { loading: isTranslating }] = useLazyQuery<{ translateSubcontractMessage: string | null }>(
        translateSubcontractMessageTemplate,
        {
            fetchPolicy: 'no-cache',
            onCompleted: ({ translateSubcontractMessage }) => {
                if (translateSubcontractMessage) updateSubcontract({ comment: translateSubcontractMessage });
            },
            onError: (error) => setTranslationError(error.message),
        },
    );

    const { loading, data } = useQuery<CommunicationData>(subcontractCommunicationTemplate, {
        fetchPolicy: 'no-cache',
        variables: { orderId: parentOrder._id },
        onCompleted(loaded) {
            if (subcontract.isCommentPrefilled) return;
            if (subcontract.comment) {
                updateSubcontract({ isCommentPrefilled: true });
                return;
            }
            fillMessage(messageSectionsOf(loaded), subcontract.messageFields, { isCommentPrefilled: true });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const sections = messageSectionsOf(data);
    const isMessageEdited = Boolean(subcontract.comment.trim()) && subcontract.comment !== subcontract.generatedComment;

    const toggleField = (field: SubcontractMessageField) => {
        const messageFields = subcontract.messageFields.includes(field)
            ? subcontract.messageFields.filter((selected) => selected !== field)
            : [...subcontract.messageFields, field];
        if (isMessageEdited) {
            setPendingFields(messageFields);
            return;
        }
        fillMessage(sections, messageFields);
    };

    const handleTranslate = () => {
        setTranslationError(undefined);
        translateMessage({
            variables: { orderId: parentOrder._id, text: subcontract.comment, language: messageLanguage },
        });
    };

    return (
        <Fragment>
            <Box mb={2}>
                <Typography variant="h1">{translate('orders:SUBCONTRACT_COMMUNICATION')}</Typography>
                <InfoText text={translate('orders:SUBCONTRACT_COMMUNICATION_INFO')} />
            </Box>
            {loading ? (
                <Loader />
            ) : (
                <Grid container spacing={2} mb={3}>
                    {MESSAGE_FIELD_OPTIONS.map((option) => (
                        <Grid item xs={12} md={6} key={option.field}>
                            <CommunicationPanel
                                title={translate(option.title)}
                                entries={sections.find((section) => section.field === option.field)?.entries ?? []}
                                emptyText={translate('orders:SUBCONTRACT_NOTHING_HERE')}
                                isSelected={subcontract.messageFields.includes(option.field)}
                                onToggle={() => toggleField(option.field)}
                                dataTest={`communication-step.panel.${option.field}`}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
            <Divider sx={{ mb: 3 }} />
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
                <Typography variant="h2">{translate('orders:SUBCONTRACT_MESSAGE_TO_SUBCONTRACTOR')}</Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="subcontract-message-language">
                            {translate('orders:SUBCONTRACT_MESSAGE_LANGUAGE')}
                        </InputLabel>
                        <Select
                            labelId="subcontract-message-language"
                            label={translate('orders:SUBCONTRACT_MESSAGE_LANGUAGE')}
                            value={messageLanguage}
                            onChange={(event) =>
                                updateSubcontract({ messageLanguage: event.target.value as LocaleLanguage })
                            }
                            data-test="communication-step.message-language"
                        >
                            {LOCALE_LANGUAGE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <LoadingButton
                        variant="contained"
                        loading={isTranslating}
                        disabled={!subcontract.comment.trim()}
                        onClick={handleTranslate}
                        data-test="communication-step.translate-message-btn"
                    >
                        {translate('orders:SUBCONTRACT_TRANSLATE_MESSAGE')}
                    </LoadingButton>
                </Box>
            </Box>
            <Box mb={2}>
                <TextField
                    variant="outlined"
                    value={subcontract.comment}
                    onChange={(event) => updateSubcontract({ comment: event.target.value })}
                    multiline
                    minRows={9}
                    fullWidth
                    inputProps={{
                        'aria-label': translate('orders:SUBCONTRACT_MESSAGE_TO_SUBCONTRACTOR'),
                        'data-test': 'communication-step.comment',
                    }}
                />
                <InfoText text={translate('orders:SUBCONTRACT_MESSAGE_TO_SUBCONTRACTOR_INFO')} />
                {subcontract.comment.trim() && subcontract.comment === subcontract.generatedComment ? (
                    <InfoText text={translate('orders:SUBCONTRACT_MESSAGE_CREATED')} />
                ) : undefined}
            </Box>
            {subcontract.selection.includeDentistName && parentOrder.dentist?.fullName ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {translate('orders:SUBCONTRACT_DENTIST_LINE_ADDED', { name: parentOrder.dentist.fullName })}
                </Alert>
            ) : undefined}
            <Box display="flex" justifyContent="space-between" mt={2}>
                <Button onClick={onBackClick} data-test="communication-step.back-btn">
                    {translate('common:BACK')}
                </Button>
                <Button variant="contained" onClick={onNextClick} data-test="communication-step.next-btn">
                    {translate('orders:NEXT_STEP')}
                </Button>
            </Box>
            <ConfirmDialog
                open={Boolean(pendingFields)}
                dialogTitle={translate('orders:SUBCONTRACT_REPLACE_MESSAGE_TITLE')}
                dialogContent={translate('orders:SUBCONTRACT_REPLACE_MESSAGE_TEXT')}
                handleClose={() => setPendingFields(null)}
                handleConfirm={() => {
                    if (pendingFields) fillMessage(sections, pendingFields);
                    setPendingFields(null);
                }}
            />
            {translationError ? <AlertSnackbar type="error" message={translationError} /> : undefined}
        </Fragment>
    );
};

export default CommunicationStep;
